import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'
import { ExportOption, Opacity, Positioning, Size } from 'constants/misc/editingOptions'
import { RatioDTO, VisualEditingTemplateOptionsDTO } from 'models/visualsEditing'

import { AxiosError, AxiosResponse } from 'axios'
import { JobDTO } from 'models/misc'
import { RoleMimetype } from 'constants/API'
import { NewWorkspaceDTO } from 'models/workspaces'
import { Nullable } from 'models/helpers'
import { DownloadArchiveResponse, SignedURLDTO } from 'models/visuals'
import { removeLastFileExtension } from 'components/common/FileAPI/fileUtils'
import { logger } from 'utils/logging'
import { JobType, useJobPoll } from 'dataQueries/job.query'
import { useAPI } from 'utils/API'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export enum VisualEditingQueryKeys {
  GET_TEMPLATE = 'GET_TEMPLATE',
  LOAD_WATERMARK_LOGO = 'LOAD_WATERMARK_LOGO',
}

enum Endpoints {
  UPDATE_VISUAL_EDITING_TEMPLATE = '/workspace/{workspaceId}/assets/template/',
  GET_VISUAL_EDITING_TEMPLATE_OPTIONS = '/workspace/{workspaceId}/assets/template/{templateId}',
  DELETE_VISUAL_EDITING_TEMPLATE = '/workspace/{workspaceId}/assets/template/{templateId}',
  TRIGGER_VISUALS_EDITING = '/assignment/{assignmentId}/visualEditing/submit',
  WATERMARK_UPLOAD = '/workspace/{workspaceId}/assets/watermark/',
  WATERMARK_LOAD = '/workspace/{workspaceId}/assets/watermark/{watermarkId}',
  WATERMARK_DELETE = '/workspace/{workspaceId}/assets/watermark/{watermarkId}',
  SUBMIT_EDITED_VISUALS_UNSUBSCRIBED = '/assignment/{assignmentId}/editedVisuals/submit-unsubscribed',
  NOTIFY_SELF_EDITED_VISUAL = '/workspace/{workspaceId}/assignment/{assignmentId}/self-edited-visuals/upload-finished?filename={filename}',
  ARCHIVE_EDITING_VISUALS = '/assignment/{assignmentId}/editedVisuals/{exportId}/archive',
  DOWNLOAD_ARCHIVE = '/assignment/{assignmentId}/visual/archive/{jobId}',
}

interface UpdateVisualEditingTemplatePayload {
  templateName?: string | null,
  watermarkId?: string | null,
  watermarkSize: Size,
  watermarkOpacity: Opacity,
  watermarkPositioning: Positioning,
  imageRatio: RatioDTO['value'],
  exportOption: ExportOption,
  workspaceId: string
}

interface VisualsEditingOptionsPayload {
  assignmentId: string
  filenames: Array<File['name']>
  applyWatermark: boolean
  watermarkSize: Size
  watermarkOpacity: number
  watermarkPositioning: Positioning
  imageRatio: RatioDTO['value']
  exportOption: ExportOption
  workspaceId: string
  watermarkId: string
}

interface VisualsEditingNoSubscriptionOptionsPayload extends Omit<VisualsEditingOptionsPayload, 'watermarkId' | 'watermarkSize' | 'watermarkOpacity' | 'watermarkPositioning' | 'workspaceId'> {
  format: ExportOption
}

// QUERIES

export function useVisualsEditingTemplateOptions(workspaceId: Nullable<NewWorkspaceDTO['id']>, templateId: Nullable<VisualEditingTemplateOptionsDTO['id']>, enabled: boolean) {
  const api = useAPI<Endpoints>()

  return useQuery<VisualEditingTemplateOptionsDTO, Error>({
    queryKey: [EntityKeys.VISUALS_EDITING, VisualEditingQueryKeys.GET_TEMPLATE, { workspaceId, templateId }],
    queryFn: () => api.get<VisualEditingTemplateOptionsDTO>(
      Endpoints.GET_VISUAL_EDITING_TEMPLATE_OPTIONS,
      {
        workspaceId: workspaceId || '',
        templateId: templateId || ''
      },
      false
    ),
    enabled
  })
}

/**
 * Query for loading watermark logo signed URL.
 * Returns null if no watermark exists (404).
 */
export function useLoadWatermarkLogo(workspaceId: Nullable<string>, watermarkId: Nullable<string>) {
  const api = useAPI<Endpoints>()

  return useQuery<SignedURLDTO | null, Error>({
    queryKey: [EntityKeys.VISUALS_EDITING, VisualEditingQueryKeys.LOAD_WATERMARK_LOGO, { workspaceId, watermarkId }],
    queryFn: async () => {
      try {
        const data = await api.get<SignedURLDTO>(
          Endpoints.WATERMARK_LOAD,
          {
            workspaceId: workspaceId || '',
            watermarkId: watermarkId || '',
          },
          false,
          {
            headers: {
              Accept: RoleMimetype.CLIENT, // TODO: Fix on BE so we can send proper accept header
            },
          }
        )

        if (!data?.signedURL) {
          const error = new Error('signedUrl is undefined, null or empty')
          logger.error('Watermark load: missing signed URL', error, {
            context: { component: 'useLoadWatermarkLogo', action: 'load' }
          })

          throw error
        }

        return data
      } catch (error) {
        if ((error as AxiosError)?.response?.status === 404) {
          return null
        }

        throw error
      }
    },
    enabled: !!workspaceId && !!watermarkId,
  })
}

// MUTATIONS

/**
 * We support only single template on FE.
 * If templateName is empty "${workspace.name} template" as a name will be used by default
 */
export const useUpdateVisualsEditingTemplate = () => {
  const api = useAPI<Endpoints>()

  return getMutation<{}, UpdateVisualEditingTemplatePayload>(
    ({ templateName = null, watermarkId = null, watermarkSize, watermarkOpacity, watermarkPositioning, imageRatio, exportOption, workspaceId }) => api.post(
      Endpoints.UPDATE_VISUAL_EDITING_TEMPLATE,
      {
        workspaceId
      },
      {
        templateName,
        watermarkId,
        watermarkSize,
        watermarkOpacity,
        watermarkPositioning,
        imageRatio,
        export: exportOption
      },
      false
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

interface UploadWatermarkLogoPayload {
  workspaceId: string
  file: File
  onProgress?: (progress: number) => void
}

interface UploadWatermarkLogoResult {
  signedUrl: string
  filename: string
}

/**
 * Mutation for uploading watermark logo.
 * Two-step flow: POST to get signed S3 URL, then PUT file to S3.
 */
export function useUploadWatermarkLogo() {
  const api = useAPI<Endpoints>()
  const client = useQueryClient()

  return useMutation<UploadWatermarkLogoResult, Error, UploadWatermarkLogoPayload>({
    mutationFn: async ({ workspaceId, file, onProgress }) => {
      const signedUrlResponse = await api.post<SignedURLDTO>(
        Endpoints.WATERMARK_UPLOAD,
        { workspaceId },
        { filename: file.name, contentType: file.type },
        false
      )

      if (!signedUrlResponse?.signedURL) {
        const error = new Error('signedUrl is undefined, null or empty')
        logger.error('Watermark upload: missing signed URL', error, {
          context: { component: 'useUploadWatermarkLogo', action: 'getSignedUrl' }
        })

        throw error
      }

      const split = signedUrlResponse.signedURL.split('?')[0].split('/')
      const filename = removeLastFileExtension(split[split.length - 1])

      await api.putExternal(signedUrlResponse.signedURL, file, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1))
          onProgress?.(percentCompleted)
        },
        headers: {
          'Content-type': file.type,
          ...signedUrlResponse.headers,
        },
      })

      return { signedUrl: signedUrlResponse.signedURL, filename }
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    },
  })
}

export const useDeleteVisualsEditingLogoAndTemplate = () => {
  const api = useAPI<Endpoints>()
  const deleteLogo = useDeleteLogo()

  return getMutation<{}, { templateId: string, workspaceId: string, watermarkId: string }>(
    ({ templateId, workspaceId }) => api.delete(
      Endpoints.DELETE_VISUAL_EDITING_TEMPLATE,
      {
        templateId,
        workspaceId
      },
      false
    ),
    (_, { workspaceId, watermarkId }) => {
      deleteLogo.mutate({ workspaceId, watermarkId }, {
        onError: (error) => {
          logger.error('Failed to delete watermark logo', error, {
            context: { component: 'useDeleteVisualsEditingLogoAndTemplate', action: 'deleteLogo' }
          })
        }
      })
    }
  )
}

/** Mutation for delete logo */
export function useDeleteLogo() {
  const api = useAPI<Endpoints>()

  return getMutation<unknown, { workspaceId: string, watermarkId: string }>(
    ({ workspaceId, watermarkId }) => api.delete(
      Endpoints.WATERMARK_DELETE,
      {
        workspaceId,
        watermarkId,
      },
      false
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

/**
 * Mutation for triggering visuals editing
 */
export const useTriggerVisualsEditing = () => {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<JobDTO>, VisualsEditingOptionsPayload>(
    ({ filenames, applyWatermark, watermarkSize, watermarkOpacity, watermarkPositioning, watermarkId, imageRatio, exportOption, workspaceId, assignmentId }) => api.post(
      Endpoints.TRIGGER_VISUALS_EDITING,
      {
        assignmentId
      },
      {
        filenames,
        applyWatermark,
        watermarkSize,
        watermarkOpacity,
        watermarkPositioning,
        imageRatio,
        export: exportOption,
        workspaceId,
        watermarkId
      },
      true
    )
  )
}

/**
 * Mutation for submitting edited visuals for users without subscription
 */
export const useSubmitEditedVisualsNoSubscription = () => {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<JobDTO>, VisualsEditingNoSubscriptionOptionsPayload>(
    ({ filenames, exportOption, assignmentId, applyWatermark, imageRatio, format }) => api.post(
      Endpoints.SUBMIT_EDITED_VISUALS_UNSUBSCRIBED,
      {
        assignmentId
      },
      {
        format,
        filenames,
        imageRatio,
        applyWatermark,
        export: exportOption,
      },
      true
    )
  )
}

/**
 * Mutation for notifying backend that self-edited visual upload is finished
 */
export const useSelfEditVisualsUploadFinished = () => {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse, { assignmentId: string, workspaceId: string, filename: string }>(
    ({ filename, assignmentId, workspaceId }) => api.post(
      Endpoints.NOTIFY_SELF_EDITED_VISUAL,
      {
        assignmentId,
        filename,
        workspaceId
      },
      {},
      true
    )
  )
}

interface ArchiveEditingVisualsPayload {
  assignmentId: string
  exportId: string
  types: string[]
  filenames: string[]
}

interface ArchiveEditingVisualsResult {
  url: string
}

/**
 * Mutation for archiving edited visuals and getting the download URL.
 * Chains 3 steps: POST archive → poll job → GET download URL.
 */
export function useArchiveEditingVisuals() {
  const api = useAPI<Endpoints>()
  const jobPoll = useJobPoll(JobType.VISUALS_EDITING)

  return useMutation<ArchiveEditingVisualsResult, Error, ArchiveEditingVisualsPayload>({
    mutationFn: async ({ assignmentId, exportId, types, filenames }) => {
      // Step 1: POST to create the archive job
      const archiveResponse = await api.post<JobDTO>(
        Endpoints.ARCHIVE_EDITING_VISUALS,
        {
          assignmentId,
          exportId,
        },
        { types, filenames },
        false,
        {
          headers: {
            Accept: RoleMimetype.CLIENT,
          },
        }
      )

      const jobId = archiveResponse?.id
      if (!jobId) {
        throw new Error('No job ID returned from archive request')
      }

      // Step 2: Poll job until finished
      await jobPoll.mutateAsync({ jobId })

      // Step 3: GET the download URL
      const downloadResponse = await api.get<DownloadArchiveResponse>(
        Endpoints.DOWNLOAD_ARCHIVE,
        {
          assignmentId,
          jobId,
        },
        false,
        {
          headers: {
            Accept: RoleMimetype.CLIENT,
          },
        }
      )

      if (!downloadResponse?.url) {
        throw new Error('No download URL returned')
      }

      return { url: downloadResponse.url }
    },
  })
}
